import type { RuntimeDataServiceId } from './contracts';
import type {
    ActiveDataBinding,
    RuntimeDataService,
    RuntimeDataServiceLinkKey,
    RuntimeDataServiceInstance,
    RuntimeResultLinkDescriptor,
} from './types';
import { isRecord } from './validationPrimitives';

function readPathValue(value: unknown, path: string): unknown {
    if (path.length === 0) {
        return value;
    }

    return path.split('.').reduce<unknown>((current, segment) => {
        if (typeof current === 'undefined' || current === null) {
            return undefined;
        }

        if (Array.isArray(current)) {
            const index = Number(segment);
            if (!Number.isInteger(index)) {
                return undefined;
            }

            return current[index];
        }

        if (isRecord(current)) {
            return current[segment];
        }

        return undefined;
    }, value);
}

type RuntimeDataServiceIndex = Map<RuntimeDataServiceId, RuntimeDataServiceInstance>;

function getRuntimeDataServiceOrThrow<TServiceId extends RuntimeDataServiceId>(
    servicesById: RuntimeDataServiceIndex,
    serviceId: TServiceId
): RuntimeDataService<TServiceId> {
    const service = servicesById.get(serviceId);
    if (!service) {
        throw new Error(`Unknown runtime data service "${serviceId}".`);
    }

    return service as RuntimeDataService<TServiceId>;
}

function getResultLinkDescriptorOrThrowForService<TServiceId extends RuntimeDataServiceId>(
    servicesById: RuntimeDataServiceIndex,
    serviceId: TServiceId,
    linkKey: RuntimeDataServiceLinkKey<TServiceId>
): RuntimeResultLinkDescriptor<TServiceId> {
    const service = getRuntimeDataServiceOrThrow(servicesById, serviceId);
    const foundLink = service.resultLinks.find((resultLink) => resultLink.key === linkKey);
    if (foundLink) {
        return foundLink;
    }

    throw new Error(`Unknown runtime result link "${linkKey}" for service "${serviceId}".`);
}

export function getResultLinkDescriptorOrThrow<TServiceId extends RuntimeDataServiceId>(
    servicesById: RuntimeDataServiceIndex,
    binding: ActiveDataBinding<TServiceId>
): RuntimeResultLinkDescriptor<TServiceId> {
    return getResultLinkDescriptorOrThrowForService(
        servicesById,
        binding.serviceId,
        binding.linkKey
    );
}

export function extractResultLinkValueOrThrow(
    servicesById: RuntimeDataServiceIndex,
    binding: ActiveDataBinding<RuntimeDataServiceId>,
    serviceResult: unknown
): unknown {
    const link = getResultLinkDescriptorOrThrow(servicesById, binding);
    const linkValue = readPathValue(serviceResult, link.path);

    if (typeof linkValue === 'undefined') {
        throw new Error(
            `Missing runtime result link path "${binding.serviceId}.${link.path}" for binding "${binding.linkKey}".`
        );
    }

    return linkValue;
}
